import { all, call, put, select } from 'redux-saga/effects';

import {
  setCustomPracticeExamHistory,
  setIsLoadingPracticeExamHistoryAction,
  setPracticeExamAction,
} from '../actions/dashboardActions';
import {
  isLoadingQuestionsPE,
  setCheckPracticeExamStatusFlag,
  setCheckPracticeExamStatusLoading,
  setCustomPracticeExam,
  setCustomPracticeExamLoading,
  setCustomPracticeExamReferences,
  setCustomPracticeExamReferencesLoading,
  setCustomPracticeExamSettings,
  setCustomPracticeExamSettingsLoading,
  setCustomPracticeExamStatus,
  setExamQuestions,
  setInitialTimePracticeExam,
  setPracticeExam,
  setPracticeExamAvailable,
  setResultsSummarized,
  setRetakeMode,
  setSubmitPracticeExamLoading,
  setSubmitPracticeExamSuccess,
  startTimerPracticeExam,
} from '../actions/practiceExamActions';
import Services from '../services';

const getPERemainingSeconds = (startedAt, durationInMillis) => {
  const duration = Number(durationInMillis);
  const finish = Number(startedAt) + duration;
  const remaining = Math.floor((finish - new Date().getTime()) / 1000);

  return Math.min(remaining, duration / 1000);
};

/**
 * Loads the weekly practice exams (the list plus the attempt in progress if any) and
 * the custom practice exam status and history
 */
export function* getWeeklyPracticeExamsSagas(action = {}) {
  try {
    yield put(setPracticeExamAvailable(false));
    yield put(setCheckPracticeExamStatusLoading(true));
    yield put(setIsLoadingPracticeExamHistoryAction(true));

    let weeklyResponse = null;
    let customStatus = null;
    let customHistory = null;

    if (action.onlyCustom) {
      [customStatus, customHistory] = yield all([
        call(Services.PracticeExamService.checkCustomPracticeExamStatus),
        call(Services.DashboardService.getCustomPracticeExamHistoryService),
      ]);
    } else {
      [weeklyResponse, customStatus, customHistory] = yield all([
        call(Services.PracticeExamService.getWeeklyPracticeExams),
        call(Services.PracticeExamService.checkCustomPracticeExamStatus),
        call(Services.DashboardService.getCustomPracticeExamHistoryService),
      ]);
    }

    if (customHistory) {
      yield put(setCustomPracticeExamHistory(customHistory));
    }

    if (weeklyResponse) {
      const exams = weeklyResponse.exams || [];

      yield put(
        setPracticeExamAction({
          exams,
          nextWeeklyGenerationAt: weeklyResponse.nextWeeklyGenerationAt,
          weeklyPracticeExamBuildInProgress:
            weeklyResponse.weeklyPracticeExamBuildInProgress,
        }),
      );
    }

    yield put(setIsLoadingPracticeExamHistoryAction(false));

    if (customStatus) {
      yield put(setCustomPracticeExamStatus(customStatus));

      if (!customStatus.notGenerated) {
        if (customStatus.practiceExamRunning) {
          yield put(setCustomPracticeExam(customStatus.practiceExamRunning));
        } else if (customStatus.availablePracticeExam) {
          yield put(setCustomPracticeExam(customStatus.availablePracticeExam));
        }

        if (customStatus.practiceExamRunning) {
          const running = customStatus.practiceExamRunning;

          yield put(setCheckPracticeExamStatusFlag('running'));
          yield put(setPracticeExam(running));
          yield put(setRetakeMode(false));
          yield put(
            setResultsSummarized(
              running.resultsDetailed
                ? JSON.parse(running.resultsDetailed)
                : [],
            ),
          );
          yield put(
            setInitialTimePracticeExam(
              getPERemainingSeconds(
                running.startedAt,
                running.examTimeInMillis,
              ),
            ),
          );
          yield put(startTimerPracticeExam(true));
          yield put(setCheckPracticeExamStatusLoading(false));

          return;
        }
      }
    }

    const running = weeklyResponse && weeklyResponse.running;

    if (running) {
      yield put(setPracticeExam(running));
      yield put(setRetakeMode(Boolean(running.retakeMode)));
      yield put(
        setResultsSummarized(
          running.resultsDetailed ? JSON.parse(running.resultsDetailed) : [],
        ),
      );
      yield put(setCheckPracticeExamStatusFlag('running'));
      yield put(
        setInitialTimePracticeExam(
          getPERemainingSeconds(running.startedAt, running.maxExamTime),
        ),
      );
      yield put(startTimerPracticeExam(true));
    } else if (weeklyResponse) {
      yield put(setRetakeMode(false));
      yield put(setCheckPracticeExamStatusFlag('available'));
      yield put(startTimerPracticeExam(false));
      yield put(setPracticeExamAvailable(true));
    }

    yield put(setCheckPracticeExamStatusLoading(false));
  } catch (exception) {
    yield put(setCheckPracticeExamStatusLoading(false));
    yield put(setIsLoadingPracticeExamHistoryAction(false));
    yield put(setPracticeExamAvailable(false));
    yield put(setRetakeMode(false));
  }
}

export function* getPracticeExamQuestionsSagas(action) {
  try {
    let response;
    if (action.isCustom) {
      response = yield call(
        Services.PracticeExamService.getCustomPracticeExamQuestions,
        action.examId,
      );
    } else {
      response = yield call(
        Services.PracticeExamService.getPracticeExamQuestions,
        action.examId,
      );
    }

    yield put(setExamQuestions(response.questions));
  } catch (exception) {
    // yield put(setExamQuestions([]));
  }
}

export function* startPracticeExamSagas(action) {
  try {
    yield put(isLoadingQuestionsPE(true));
    yield put(setCheckPracticeExamStatusFlag(''));

    const response = action.isCustom
      ? yield call(
          Services.PracticeExamService.startCustomPracticeExam,
          Date.now(),
        )
      : yield call(
          Services.PracticeExamService.startWeeklyPracticeExam,
          action.practiceExamId,
          action.userPracticeExamId,
        );
    if (response.notDeveloped) {
      yield put(setCheckPracticeExamStatusFlag('not-developed'));
    } else {
      yield put(setExamQuestions(response.questions));
      yield put(setPracticeExam(response));

      const finish = new Date(Number(response.startedAt));
      finish.setTime(finish.getTime() + Number(response.examTime));

      const now = new Date();

      let differenceInSeconds = (finish.getTime() - now.getTime()) / 1000;

      if (differenceInSeconds > Number(response.examTime) / 1000) {
        differenceInSeconds = Number(response.examTime) / 1000;
      }
      yield put(setRetakeMode(response.retakeMode));
      yield put(setInitialTimePracticeExam(differenceInSeconds));
      yield put(startTimerPracticeExam(true));
    }
    yield put(isLoadingQuestionsPE(false));
  } catch (exception) {
    yield put(setCheckPracticeExamStatusFlag('not-available'));
    yield put(isLoadingQuestionsPE(false));
  }
}

export function* updatePracticeExamResultsSagas(action) {
  try {
    const { examId, results, isCustom } = action;
    // 1) Verify that there are no duplicate questions
    const selectExamQuestions = (state) =>
      state.practiceExamReducer?.get('questions');
    const toNum = (v) => Number(v);
    const isFiniteNum = (v) => Number.isFinite(v);
    const dedupeKeepLast = (r) => {
      const m = new Map();
      for (const item of r || []) {
        const q = toNum(item?.question);
        const a = toNum(item?.answer);
        if (isFiniteNum(q) && isFiniteNum(a)) {
          m.set(q, a);
        }
      }
      return Array.from(m.entries()).map(([question, answer]) => ({
        question,
        answer,
      }));
    };

    const deduped = dedupeKeepLast(results);

    // 2) Filter by belonging to the current exam
    const examQuestions = yield select(selectExamQuestions);

    const allowedIds = new Set(
      (examQuestions || []).map((q) => toNum(q?.id)).filter(isFiniteNum),
    );

    // If there are no questions loaded, we send deduped (fallback). If there are, we filter
    const sanitized = allowedIds.size
      ? deduped.filter(({ question }) => allowedIds.has(toNum(question)))
      : deduped;

    const response = isCustom
      ? yield call(
          Services.PracticeExamService.updateCustomPracticeExam,
          examId,
          sanitized,
        )
      : yield call(
          Services.PracticeExamService.updatePracticeExam,
          examId,
          sanitized,
        );

    yield put(setPracticeExam(response));
  } catch (exception) {}
}

export function* submitPracticeExamResultsSagas(action) {
  try {
    yield put(setSubmitPracticeExamLoading(true));
    const {
      createdAt,
      finishStatus,
      finishedAt,
      id,
      practiceExam,
      resultsDetailed,
      resultsSummarized,
      retakeResultsDetailed,
      retakeResultsSummarized,
      retakeStartedAt,
      startedAt,
      updatedAt,
      user,
    } = action.examId;

    const filteredExamId = {
      createdAt,
      finishStatus,
      finishedAt,
      id,
      practiceExam,
      resultsDetailed,
      resultsSummarized,
      retakeResultsDetailed,
      retakeResultsSummarized,
      retakeStartedAt,
      startedAt,
      updatedAt,
      user,
    };
    let response;
    if (action.isCustom) {
      response = yield call(
        Services.PracticeExamService.submitCustomPracticeExam,
        filteredExamId,
        action.results,
      );
    } else {
      response = yield call(
        Services.PracticeExamService.submitPracticeExam,
        filteredExamId,
        action.results,
      );
    }

    yield put(setPracticeExam(response));
    yield put(setExamQuestions(response.questions));
    yield put(startTimerPracticeExam(false));
    yield put(setResultsSummarized(response.resultsDetailed));
    yield put(setSubmitPracticeExamSuccess(true));
    yield put(setSubmitPracticeExamLoading(false));
  } catch (exception) {
    yield put(setSubmitPracticeExamSuccess(false));
    yield put(setSubmitPracticeExamLoading(false));
  }
}

export function* practiceExamResultsSagas(action) {
  try {
    yield put(setSubmitPracticeExamLoading(true));
    let response;
    if (action.isCustom) {
      response = yield call(
        Services.PracticeExamService.resultCustomPracticeExam,
        action.examId,
      );
    } else {
      response = yield call(
        Services.PracticeExamService.resultPracticeExam,
        action.examId,
        action.retake,
      );
    }
    const info = response.practiceExamUser;
    yield put(setPracticeExam(info));
    yield put(setExamQuestions(info.questions));
    yield put(startTimerPracticeExam(false));
    yield put(setResultsSummarized(info.resultsDetailed));
    yield put(setSubmitPracticeExamLoading(false));
  } catch (exception) {
    yield put(setSubmitPracticeExamLoading(false));
  }
}

export function* generateCustomPracticeExamSaga(action) {
  try {
    yield put(setCustomPracticeExamLoading(true));
    yield call(
      Services.PracticeExamService.generateCustomPracticeExam,
      action.chapterIds,
      action.isRegeneration,
    );
    yield call(getWeeklyPracticeExamsSagas, { onlyCustom: true });
    yield put(setCustomPracticeExamLoading(false));
  } catch (e) {
    yield put(setCustomPracticeExamLoading(false));
  }
}

export function* getCustomPracticeExamReferencesSaga(action) {
  try {
    yield put(setCustomPracticeExamReferencesLoading(true));
    const response = yield call(
      Services.PracticeExamService.getCustomPracticeExamReferences,
      action.rateId,
    );
    if (response.references) {
      yield put(setCustomPracticeExamReferences(response.references));
    }
    yield put(setCustomPracticeExamReferencesLoading(false));
  } catch (exception) {
    yield put(setCustomPracticeExamReferencesLoading(false));
  }
}

export function* getCustomPracticeExamSettingsSaga() {
  try {
    yield put(setCustomPracticeExamSettingsLoading(true));

    const response = yield call(
      Services.PracticeExamService.getCustomPracticeExamSettings,
    );

    if (response) {
      yield put(setCustomPracticeExamSettings(response));
    }
  } catch (exception) {
  } finally {
    yield put(setCustomPracticeExamSettingsLoading(false));
  }
}
